import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { UserPlus, Gift, ChevronDown, X, Check } from 'lucide-react';
import PasswordInput from '@/components/marketplace/PasswordInput';
import TrustPanel from '@/components/marketplace/TrustPanel';

const REF_STORAGE_KEY = 'indusfact_ref_code';

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    name: '', email: '', password: '', mobile: '', company_name: '', referral_code: ''
  });
  const [loading, setLoading] = useState(false);
  const [refApplied, setRefApplied] = useState(false); // auto-applied via cookie/URL
  const [refPreview, setRefPreview] = useState(null); // {referrer_name, referrer_company}
  const [showRefManual, setShowRefManual] = useState(false); // collapsible "Have a code?"

  // Auto-detect referral code from sessionStorage (cookie) or ?ref=XYZ URL param
  useEffect(() => {
    let code = null;
    try {
      code = sessionStorage.getItem(REF_STORAGE_KEY);
    } catch { /* ignore */ }
    const urlCode = searchParams.get('ref');
    if (urlCode) code = urlCode.toUpperCase();
    if (code) {
      setForm((f) => ({ ...f, referral_code: code }));
      setRefApplied(true);
      validateRefCode(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateRefCode = async (code) => {
    if (!code || code.length < 3) { setRefPreview(null); return; }
    try {
      const { data } = await api.get(`/referrals/lookup/${encodeURIComponent(code)}`);
      if (data.ok) setRefPreview(data);
      else setRefPreview({ invalid: true });
    } catch {
      setRefPreview({ invalid: true });
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleManualRefChange = (e) => {
    const val = e.target.value.toUpperCase().trim();
    setForm({ ...form, referral_code: val });
    setRefApplied(false); // manual entry
    if (val.length === 0) { setRefPreview(null); return; }
    // Debounce a tiny bit
    clearTimeout(window.__refLookup);
    window.__refLookup = setTimeout(() => validateRefCode(val), 350);
  };

  const clearRef = () => {
    setForm({ ...form, referral_code: '' });
    setRefApplied(false);
    setRefPreview(null);
    try { sessionStorage.removeItem(REF_STORAGE_KEY); } catch { /* ignore */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) return toast.error('Name, email, and password are required');
    if (form.password.length < 6) return toast.error('Password must be at least 6 characters');
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.referral_code) delete payload.referral_code;
      await register(payload);
      try { sessionStorage.removeItem(REF_STORAGE_KEY); } catch { /* ignore */ }
      toast.success(
        form.referral_code && refPreview?.ok !== false && !refPreview?.invalid
          ? 'Account created — welcome bonus applied!'
          : 'Account created — welcome to IndusFact!'
      );
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-[80vh] indus-container py-10 flex items-center justify-center'>
      <Card className='w-full max-w-lg p-8'>
        <div className='flex items-center gap-2 mb-6'>
          <span className='inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white border overflow-hidden'>
            <img src='/indusfact-logo.png' alt='IndusFact' className='h-8 w-8 object-contain' />
          </span>
          <div>
            <div className='font-semibold text-lg'>Create your IndusFact account</div>
            <div className='text-xs text-muted-foreground'>Free B2B account · Post listings and manage inquiries</div>
          </div>
        </div>

        {/* Auto-applied referral pill */}
        {refApplied && form.referral_code && (
          <div
            className='mb-4 rounded-lg border border-primary/30 bg-primary/10 p-3 text-sm flex items-start gap-2'
            data-testid='referral-applied-pill'
          >
            <Gift className='h-4 w-4 text-primary shrink-0 mt-0.5' />
            <div className='flex-1 min-w-0'>
              <div className='font-semibold text-foreground'>
                Referral code applied: <code className='text-primary'>{form.referral_code}</code>
              </div>
              {refPreview?.ok && (refPreview.referrer_name || refPreview.referrer_company) && (
                <div className='text-xs text-muted-foreground mt-0.5'>
                  Invited by <strong>{refPreview.referrer_name || refPreview.referrer_company}</strong>. You&apos;ll receive a welcome bonus after signup.
                </div>
              )}
              {refPreview?.invalid && (
                <div className='text-xs text-[hsl(var(--danger))] mt-0.5'>Code not recognised — signup will still work without the bonus.</div>
              )}
            </div>
            <button type='button' onClick={clearRef} className='text-muted-foreground hover:text-foreground shrink-0' data-testid='clear-referral-btn' aria-label='Clear referral code'>
              <X className='h-4 w-4' />
            </button>
          </div>
        )}

        <form onSubmit={submit} className='space-y-4'>
          <div>
            <Label>Your name *</Label>
            <Input data-testid='register-name-input' value={form.name} onChange={set('name')} placeholder='Ramesh Kumar' />
          </div>
          <div>
            <Label>Email *</Label>
            <Input data-testid='register-email-input' type='email' value={form.email} onChange={set('email')} placeholder='you@company.com' />
          </div>
          <div>
            <Label>Password *</Label>
            <PasswordInput
              data-testid='register-password-input'
              value={form.password}
              onChange={set('password')}
              placeholder='Min 6 characters'
              autoComplete='new-password'
            />
          </div>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
            <div>
              <Label>Mobile</Label>
              <Input data-testid='register-mobile-input' value={form.mobile} onChange={set('mobile')} placeholder='+91-98XXXXXX' />
            </div>
            <div>
              <Label>Company name</Label>
              <Input data-testid='register-company-input' value={form.company_name} onChange={set('company_name')} placeholder='ACME Industries' />
            </div>
          </div>

          {/* Collapsible: 'Have a referral code?' — only shown when NOT already auto-applied */}
          {!refApplied && (
            <div className='pt-1'>
              {!showRefManual ? (
                <button
                  type='button'
                  onClick={() => setShowRefManual(true)}
                  className='inline-flex items-center gap-1 text-xs text-primary hover:underline'
                  data-testid='toggle-manual-referral'
                >
                  <Gift className='h-3.5 w-3.5' /> Have a referral code?
                  <ChevronDown className='h-3.5 w-3.5' />
                </button>
              ) : (
                <div className='rounded-md border p-3 bg-muted/30' data-testid='manual-referral-block'>
                  <Label className='text-xs'>Referral code (optional)</Label>
                  <Input
                    value={form.referral_code}
                    onChange={handleManualRefChange}
                    placeholder='e.g. RAMESH24'
                    className='mt-1 tracking-wider uppercase'
                    data-testid='register-referral-input'
                  />
                  {refPreview?.ok && form.referral_code && (
                    <div className='mt-1.5 text-xs text-[hsl(var(--verified))] flex items-center gap-1'>
                      <Check className='h-3 w-3' /> Valid code from {refPreview.referrer_name || refPreview.referrer_company || 'an IndusFact member'}
                    </div>
                  )}
                  {refPreview?.invalid && form.referral_code && (
                    <div className='mt-1.5 text-xs text-[hsl(var(--danger))]'>Code not recognised — you can still register without it.</div>
                  )}
                </div>
              )}
            </div>
          )}

          <Button className='w-full' type='submit' disabled={loading} data-testid='register-submit-button'>
            <UserPlus className='h-4 w-4 mr-1.5' /> {loading ? 'Creating account…' : 'Create account'}
          </Button>
        </form>

        <div className='mt-4 text-xs text-muted-foreground text-center'>
          Already have an account?{' '}
          <Link to='/login' className='text-primary hover:underline'>Sign in</Link>
        </div>
        <div className='mt-4 text-[11px] text-muted-foreground'>
          By registering you agree to IndusFact&apos;s Terms and acknowledge our Privacy Policy. Company identity is kept private on public pages.
        </div>

        <TrustPanel />
      </Card>
    </div>
  );
};

export default Register;
